import type { PlayerRef } from '@remotion/player';
import { cn } from '@crabs-ai/ds-react';
import { useMemo, type FC } from 'react';
import { usePlayerOutputAudioDb } from './use-player-output-audio-db';

const MIN_DB = -60;
const MAX_DB = 0;
const SEGMENTS = 24;

/** 상단→하단: 0 dBFS ~ −60 dBFS 고정 척도 */
const DB_SCALE_TICKS = [0, -12, -24, -36, -48, -60] as const;

function dbToFillRatio(db: number): number {
  const t = (db - MIN_DB) / (MAX_DB - MIN_DB);
  return Math.max(0, Math.min(1, t));
}

export const TimelineAudioLevelMeter: FC<{
  playerRef: React.RefObject<PlayerRef | null>;
  className?: string;
}> = ({ playerRef, className }) => {
  const dbFs = usePlayerOutputAudioDb(playerRef);
  const fill = dbToFillRatio(dbFs);

  const activeCount = useMemo(() => Math.round(fill * SEGMENTS), [fill]);

  return (
    <div
      className={cn(
        'pointer-events-none flex h-full min-h-0 w-full min-w-0 flex-row items-stretch gap-2 px-1.5 py-1',
        className,
      )}
      aria-hidden
    >
      <div
        className="text-sys-style-neutral-assistive flex min-h-0 min-w-0 shrink flex-col justify-between py-px text-[7px] leading-none font-medium tabular-nums"
        aria-hidden
      >
        {DB_SCALE_TICKS.map((db) => (
          <span
            key={db}
            className="whitespace-nowrap"
          >
            {db}
          </span>
        ))}
      </div>
      {/* 막대 폭: 18px 대비 +10% — 전체 열 너비는 timeline.tsx clamp(×0.9) */}
      <div className="flex min-h-0 w-5 min-w-[20px] shrink-0 flex-col-reverse gap-px">
        {Array.from({ length: SEGMENTS }, (_, i) => {
          const fromBottom = i;
          const isOn = fromBottom < activeCount;
          const hue =
            fromBottom < SEGMENTS * 0.55
              ? 'bg-emerald-500/90'
              : fromBottom < SEGMENTS * 0.8
                ? 'bg-amber-400/90'
                : 'bg-red-500/90';
          return (
            <div
              key={i}
              className={cn(
                'min-h-[2px] w-full flex-1 rounded-[1px]',
                isOn ? hue : 'bg-sys-style-neutral-default/35',
              )}
            />
          );
        })}
      </div>
    </div>
  );
};
